import { useEffect, useMemo, useState } from 'react';
import { Badge, Card, TextInput, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import type { ServiceOrder, ServiceVehicle } from '../../api/types';
import { fetchServiceVehicle } from '../../api/service';
import { t } from '../../lib/i18n';

export interface VehicleListRow { equip_id: number; desc: string; plate: string; customer: string; }
export function vehicleList(orders: ServiceOrder[]): VehicleListRow[] {
  const m = new Map<number, VehicleListRow>();
  for (const o of orders) {
    if (!o.equip_id) continue;
    if (!m.has(o.equip_id)) m.set(o.equip_id, { equip_id: o.equip_id, desc: o.equip_desc, plate: o.equip_plate, customer: o.anum_name });
  }
  return [...m.values()].sort((a, b) => a.desc.localeCompare(b.desc));
}

export type BadgeColor = 'green' | 'amber' | 'red' | 'none';
export function warrantyBadge(dateStr: string, today: Date): BadgeColor {
  if (!dateStr) return 'none';
  const days = (new Date(dateStr).getTime() - today.getTime()) / 86400000;
  if (days < 0) return 'red';
  if (days <= 60) return 'amber';
  return 'green';
}
const BADGE = { green: 'emerald', amber: 'amber', red: 'red', none: 'gray' } as const;
const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

export default function Vehiculos({ orders, sel, onSelect }: { orders: ServiceOrder[]; sel: number | null; onSelect: (equip: number) => void }) {
  const [q, setQ] = useState('');
  const [veh, setVeh] = useState<ServiceVehicle | null>(null);
  const list = useMemo(() => vehicleList(orders), [orders]);
  const filtered = useMemo(() => { const s = q.toLowerCase();
    return list.filter((v) => !s || v.desc.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s) || v.customer.toLowerCase().includes(s)); }, [list, q]);
  useEffect(() => { if (!sel) { setVeh(null); return; } let a = true; setVeh(null);
    fetchServiceVehicle(sel).then((r) => a && setVeh(r)).catch(() => {}); return () => { a = false; }; }, [sel]);
  const c = t.service.cockpit;
  const today = new Date();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1">
        <TextInput placeholder={c.vehicleSearch} value={q} onValueChange={setQ} />
        <div className="mt-2 space-y-1 max-h-[70vh] overflow-y-auto">
          {filtered.map((v) => (
            <button key={v.equip_id} onClick={() => onSelect(v.equip_id)}
              className={`block w-full text-left text-sm p-2 rounded hover:bg-tremor-background-muted ${sel === v.equip_id ? 'bg-tremor-background-muted' : ''}`}>
              <div className="font-medium">{v.desc}</div><div className="text-xs text-tremor-content">{v.plate} · {v.customer}</div>
            </button>
          ))}
        </div>
      </Card>
      <div className="lg:col-span-2 space-y-4">
        {!veh ? <Card><Text>{c.vehicleSearch}</Text></Card> : (<>
          <Card>
            <Title className="text-base">{veh.equip.mfg} {veh.equip.model} {veh.equip.mod_year}</Title>
            <Text className="text-sm">{veh.equip.license} · VIN {veh.equip.vin || '—'} · {c.meters}: {veh.equip.cur_miles.toLocaleString('es-MX')}</Text>
            <div className="flex gap-2 mt-2">
              <Badge color={BADGE[warrantyBadge(veh.equip.warr_date, today)]}>{c.warranty}: {veh.equip.warr_date || '—'}</Badge>
              <Badge color={BADGE[warrantyBadge(veh.equip.contract_exp, today)]}>{c.contract}: {veh.equip.contract_exp || '—'}</Badge>
            </div>
          </Card>
          <Card>
            <Title className="text-base">{c.history}</Title>
            <Table className="mt-2"><TableHead><TableRow>
              <TableHeaderCell>{t.service.cols.date}</TableHeaderCell><TableHeaderCell>{t.service.cols.tnum}</TableHeaderCell>
              <TableHeaderCell className="text-right">{t.service.cols.jobs}</TableHeaderCell><TableHeaderCell className="text-right">{t.service.cols.revenue}</TableHeaderCell>
            </TableRow></TableHead><TableBody>
              {veh.history.map((h) => (<TableRow key={`${h.sid}-${h.tnum}`}>
                <TableCell>{h.work_date}</TableCell><TableCell>#{h.tnum}</TableCell>
                <TableCell className="text-right tabular-nums">{h.jobs}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtMxn.format(h.total_mxn)}</TableCell>
              </TableRow>))}
            </TableBody></Table>
          </Card>
        </>)}
      </div>
    </div>
  );
}
